import { ParamMap } from '@angular/router';

import {
  ACTIVATION_DEEP_LINK_KEY,
  ACTIVATION_PENDING_PRICE_KEY,
  ACTIVATION_QUERY_PARAM,
} from '../tenant-lifecycle.config';

/** Query parameters Stripe appends to the return_url after a redirect-based confirmation. */
const STRIPE_RETURN_PARAMS = ['setup_intent', 'setup_intent_client_secret', 'redirect_status'] as const;

/** Every parameter that is removed from the address once the modal opened. */
export const ACTIVATION_URL_PARAMS: readonly string[] = [ACTIVATION_QUERY_PARAM, ...STRIPE_RETURN_PARAMS];

/** Back from a Stripe redirect (bank authorisation): finish the activation with this SetupIntent. */
export interface ActivationRedirect {
  setupIntentId: string;
  succeeded: boolean;
  /** The price chosen before the redirect; null when the session lost it. */
  priceId: string | null;
}

export type ActivationDeepLink = { kind: 'open' } | { kind: 'redirect'; redirect: ActivationRedirect };

/** What the query of the current route asks for: open the modal, finish a redirect, or nothing. */
export function readActivationDeepLink(params: ParamMap): ActivationDeepLink | null {
  const setupIntentId = params.get('setup_intent');
  const status = params.get('redirect_status');
  if (setupIntentId !== null && setupIntentId !== '' && status !== null) {
    return {
      kind: 'redirect',
      redirect: { setupIntentId, succeeded: status === 'succeeded', priceId: readPendingPrice() },
    };
  }
  return params.get(ACTIVATION_QUERY_PARAM) === '1' ? { kind: 'open' } : null;
}

/**
 * Runs before the login: remembers ?activate=1 of the address the browser
 * opened, so the modal still opens when the identity provider comes back to
 * the root without it. Only ever sets the marker; the flow consumes it.
 */
export function rememberActivationDeepLink(search: string): void {
  if (new URLSearchParams(search).get(ACTIVATION_QUERY_PARAM) !== '1') {
    return;
  }
  try {
    sessionStorage.setItem(ACTIVATION_DEEP_LINK_KEY, '1');
  } catch {
    // Without storage the link only works for a session that is already signed in.
  }
}

/** Whether ?activate=1 was remembered before the login; forgets it. */
export function consumeRememberedActivationDeepLink(): boolean {
  try {
    const remembered = sessionStorage.getItem(ACTIVATION_DEEP_LINK_KEY) === '1';
    sessionStorage.removeItem(ACTIVATION_DEEP_LINK_KEY);
    return remembered;
  } catch {
    return false;
  }
}

/**
 * Where Stripe sends the browser back after a redirect: the current admin
 * page with ?activate=1, so the modal opens again and finishes the activation.
 */
export function activationReturnUrl(href: string): string {
  const url = new URL(href);
  STRIPE_RETURN_PARAMS.forEach((param) => url.searchParams.delete(param));
  url.searchParams.set(ACTIVATION_QUERY_PARAM, '1');
  return url.toString();
}

export function storePendingPrice(priceId: string): void {
  try {
    sessionStorage.setItem(ACTIVATION_PENDING_PRICE_KEY, priceId);
  } catch {
    // Without storage a redirect comes back to the plan step instead.
  }
}

export function clearPendingPrice(): void {
  try {
    sessionStorage.removeItem(ACTIVATION_PENDING_PRICE_KEY);
  } catch {
    // Nothing stored then.
  }
}

function readPendingPrice(): string | null {
  try {
    const stored = sessionStorage.getItem(ACTIVATION_PENDING_PRICE_KEY);
    return stored !== null && stored !== '' ? stored : null;
  } catch {
    return null;
  }
}
